import orderBy from "lodash/orderBy";
import { sleep } from "@teaming/common-util";
import { alertFactory } from "@teaming/common-ui";
import { makeMarkMap } from "@teaming/common-team";

import * as listeners from "./listeners";
import { pristineState } from "./state";
import { mapFormToGoal, mapGoalToForm, mapKeyResultToForm, mapActivityToFeed } from "../mapping";
import { newGoalForm, newKeyResultForm } from "../forms";
import { DateTime } from "luxon";

export default function goalStoreFactory( {
	storeName,
	goalsStoreName,
	foundation,
	appChannels,
	/** { store: { navigateToGoals:Function, navigateToGoal:Function, inGoalRoute:Function.<Boolean>, }, ...navigation } */
	navigation,
} ) {
	const { store, i18n, ioSocket, ioTeam, ioGokr, } = foundation();

	const { success, error, info, } = alertFactory( "common-gokr.stores.Goal", i18n.global.t );
	const alerts = { success, error, info, };

	const { ioChannel, } = appChannels;
	listeners.configureSockets( {
		storeName,
		goalsStoreName,
		store,
		navigation,
		ioSocket,
		ioGokr,
		alerts,
	} );

	listeners.configureChannels( {
		storeName,
		goalsStoreName,
		store,
		navigation,
		ioChannel,
		alerts,
	} );

	const markMap = makeMarkMap( i18n );

	return {
		namespaced: true,
		state: pristineState( i18n ),
		getters: {
			userProfile: _ => store.getters[ "host-userProfile/userProfile" ],
			timezone: _ => store.getters[ "host-userProfile/timezone" ],
			context: state => {
				const { teamId, goalId, } = state.goalForm;
				return {
					teamId,
					goalId,
				};
			},
			isBusy: state => state.isBusy,
			isDisabled: state => state.isDisabled,
			selectedPanel: state => state.selectedPanel,
			focusedField: state => state.focusedField,
			possibleTeamOptions( state, getters ) {
				const { context, } = getters;
				const { teamId, } = context;
				const options = state.myTeams
					.filter( team => team.id === teamId || !team.archived )
					.map( team => {
						return {
							text: team.extendedName,
							value: team.id,
							mark: markMap.get( team.type ),
							sortKey: team.name.toLowerCase(),
						};
					} );
				return orderBy( options, [ "sortKey", ], [ "asc", ] );
			},
			possibleKeyResultOwners( state ) {
				const { myTeams, goalForm, } = state;
				const { assignedTeamId, } = goalForm;
				const assignedTeam = myTeams.find( team => team.id === assignedTeamId );
				if ( !assignedTeam ) {
					return [];
				}
				const possibleOwners = assignedTeam.members.map( member => ( {
					...member,
					sortKey: member.displayName.toLowerCase(),
				} ) );
				return orderBy( possibleOwners, [ "sortKey", ], [ "asc", ] );
			},
			goalForm: state => state.goalForm,
			keyResultForms( state ) {
				const { goalForm, } = state;
				const { capabilities: goalCapabilities, } = goalForm;
				const mappedForms = state.keyResultForms.map( keyResultForm => ( {
					...keyResultForm,
					parentGoalName: goalForm.title,
					// for display
					startDate: goalForm.startDate,
					endDate: goalForm.endDate,
					// for calculation
					dueDate: DateTime.fromISO( goalForm.endDateTime )
						.toFormat( "MMM d" ),
					capabilities: {
						...keyResultForm.capabilities,
						edit: goalCapabilities.edit,
					},
				} ) );
				return orderBy( mappedForms, [
					( { name, } ) => name.toLowerCase(),
					"sortOrder",
				], [
					"asc",
					"asc",
				] );
			},
			savePermitted( state ) {
				// A goal can be saved if a) all goal and key result fields have been touched
				// (this is ensured when the save is attempted), and b) all goal and key result
				// error messages are empty.

				const { goalForm, keyResultForms, } = state;

				const invalidKeyResultForm = keyResultForms.find( keyResultForm => {
					const { allFieldsTouched, allFieldsValid, } = keyResultForm;
					return !( allFieldsTouched && allFieldsValid );
				} );

				return goalForm.allFieldsTouched &&
					goalForm.allFieldsValid &&
					!invalidKeyResultForm;
			},
			promptOnDiscard( state ) {
				const { goalForm, keyResultForms, } = state;
				return !!( goalForm.isTouched || keyResultForms.find( form => form.isTouched ) );
			},
			materializedGoal( state ) {
				const { goalForm, keyResultForms, } = state;
				return mapFormToGoal( goalForm, keyResultForms );
			},
			goalActivity( state, getters ) {
				const { goalActivity, } = state;
				const { timezone, } = getters;

				const mappedActivity = mapActivityToFeed( goalActivity, timezone );
				return orderBy( mappedActivity, [ "sortKey", ], [ "desc", ] );
			},
			activityComment: state => state.activityComment,
		},
		mutations: {
			clearAll( state ) {
				Object.assign( state, pristineState( i18n ) );
			},
			pause( state, background = false ) {
				state.isBusy = true;
				state.isDisabled = !background;
			},
			resume( state ) {
				state.isBusy = false;
				state.isDisabled = false;
			},
			receiveMyTeams( state, myTeams ) {
				const selectedTeam = store.getters[ `${ goalsStoreName }/selectedTeam` ];
				const { goalForm, } = state;

				const patchedTeams = myTeams.map( myTeam => {
					return {
						...myTeam,
						sortKey: myTeam.name.toLowerCase(),
					};
				} );

				state.myTeams = orderBy( patchedTeams, [ "sortKey", ], [ "asc", ] );

				if ( selectedTeam.id && !goalForm.assignedTeamId ) {
					// if this goal doesn't have a team, try to automatically assign it to the
					// team selected in the goals store (goal list team) if possible
					const {
						id: assignedTeamId,
						name: assignedTeamName,
					} = selectedTeam;

					state.goalForm = newGoalForm( {
						...goalForm,
						assignedTeamId,
						assignedTeamName,
					}, i18n, { touchAll: state.saveAttempted, } );
				} else if ( state.myTeams.length && !goalForm.assignedTeamId ) {
					// if this goal STILL doesn't have a team, assign it to the first of
					// the loaded teams, if possible
					const {
						id: assignedTeamId,
						name: assignedTeamName,
					} = state.myTeams[ 0 ];

					state.goalForm = newGoalForm( {
						...goalForm,
						assignedTeamId,
						assignedTeamName,
					}, i18n, { touchAll: state.saveAttempted, } );
				}
			},
			receiveGoal( state, goal ) {
				state.goalForm = newGoalForm( mapGoalToForm( goal ), i18n );

				state.keyResultForms = goal.keyResults.map( keyResult => {
					return newKeyResultForm( mapKeyResultToForm( keyResult ), i18n );
				} );
			},
			receiveKeyResults( state, goal ) {
				state.keyResultForms = goal.keyResults.map( keyResult => {
					return newKeyResultForm( mapKeyResultToForm( keyResult ), i18n );
				} );

				// progress (percentCompleted) is a computed value that is a function of
				// all KR completion percentages, so it must also be updated here
				state.goalForm = newGoalForm( {
					...state.goalForm,
					progress: goal.percentCompleted,
				}, i18n, { touchAll: state.saveAttempted, } );
			},
			receiveGoalActivity( state, goalActivity ) {
				state.goalActivity = goalActivity;
			},
			selectPanel( state, panel ) {
				state.selectedPanel = panel;
				state.activityComment = "";
			},
			assignGoalToTeam( state, teamId ) {
				if ( state.goalForm.assignedTeamId === teamId ) {
					return;
				}

				const team = state.myTeams.find( myTeam => myTeam.id === teamId ) || {};
				const { name = "", } = team;

				state.goalForm = newGoalForm( {
					...state.goalForm,
					assignedTeamId: teamId,
					assignedTeamName: name,
				}, i18n, { touchAll: state.saveAttempted, } );
			},
			ensureValidation( state ) {
				state.saveAttempted = true;
				const { goalForm, keyResultForms, } = state;
				state.goalForm = newGoalForm(
					goalForm, i18n, { touchAll: state.saveAttempted, }
				);
				state.keyResultForms = keyResultForms.map( keyResultForm => {
					return newKeyResultForm(
						keyResultForm, i18n, { touchAll: state.saveAttempted, }
					);
				} );
			},
			updateGoalForm( state, form ) {
				state.goalForm = newGoalForm( {
					...state.goalForm,
					...form,
				}, i18n, { touchAll: state.saveAttempted, } );
			},
			commitGoalForm( state, { goal, revision, } ) {
				const { goalId, revision: currentRevision, } = state.goalForm;

				// if the goal already exists, and this update is for a different
				// goal, ignore the update
				if ( goalId && ( goalId !== goal.id ) ) {
					return;
				}

				// if the revision has changed, the goal was updated before
				// the save request could be completed, so we need to wait
				// until the revision matches
				if ( currentRevision !== revision ) {
					return;
				}

				// update the goal, but preserve its focused field
				state.goalForm = newGoalForm( mapGoalToForm( goal ), i18n );

				state.keyResultForms = goal.keyResults.map( keyResult => {
					return newKeyResultForm( mapKeyResultToForm( keyResult ), i18n );
				} );
			},
			stageNewKeyResultForm( state ) {
				const userProfile = store.getters[ "host-userProfile/userProfile" ];
				const { personId: ownerId, displayName: ownerName, } = userProfile;

				// intentionally ignore state.saveAttempted as this is
				// a newly added KR, and should be in an untouched
				// state
				const newForm = newKeyResultForm( {
					ownerId,
					ownerName,
				}, i18n );

				state.keyResultForms = [
					...state.keyResultForms,
					newForm,
				];

				// revalidate the goal form since the KR count has changed
				state.goalForm = newGoalForm( {
					...state.goalForm,
					keyResultCount: state.keyResultForms.length,
				}, i18n, { touchAll: state.saveAttempted, } );

				// set the focused field to the new key result name
				const { formId, } = newForm;
				state.focusedField = `${ formId }.name:0`;
			},
			discardKeyResultForm( state, formId ) {
				state.keyResultForms = state.keyResultForms.filter(
					form => form.formId !== formId
				);

				// revalidate the goal form since the KR count has changed, and
				// focus its title field
				state.goalForm = newGoalForm( {
					...state.goalForm,
					keyResultCount: state.keyResultForms.length,
				}, i18n, { touchAll: state.saveAttempted, } );

				// set the focused field to the new goal title
				const { formId: goalFormId, title, } = state.goalForm;
				state.focusedField = `${ goalFormId }.title:${ title.length }`;
			},
			updateKeyResultForm( state, form ) {
				state.keyResultForms = state.keyResultForms.map( keyResultForm => {
					if ( keyResultForm.formId !== form.formId ) {
						return keyResultForm;
					}

					const { myTeams, goalForm, } = state;
					const assignedTeam = myTeams.find( team => team.id === goalForm.assignedTeamId );
					const { members, } = assignedTeam;
					const member = members.find( teamMember => teamMember.personId === form.ownerId );

					const {
						displayName: ownerName,
						email: ownerEmail,
						picture: ownerAvatar,
					} = member;

					return newKeyResultForm( {
						...form,
						ownerName,
						ownerEmail,
						ownerAvatar,
					}, i18n, {
						resetValues: form.type !== keyResultForm.type,
						touchAll: state.saveAttempted,
					} );
				} );
			},
			focusField( state, { formId, focusedFieldName, selectionStart = 0, } ) {
				state.focusedField = `${ formId }.${ focusedFieldName }:${ selectionStart }`;
			},
			updateActivityComment( state, form ) {
				state.activityComment = form.comment;
			},
		},
		actions: {
			/**
			 * My new goal: (no IDs)
			 * Update my goal: goalId
			 * My new 1:1 goal: teamId
			 * Update my 1:1 goal: teamId, goalId
			 * My new team goal: teamId
			 * Update my team goal: teamId, goalId
			 */
			async initialize( { commit, }, { teamId = "", goalId = "", } = {} ) {
				try {
					commit( "pause" );
					const promises = [
						ioTeam.fetchAllMyTeams( { limit: Number.MAX_SAFE_INTEGER, } ),
						goalId ? ioGokr.fetchGoal( { goalId, } ) : Promise.resolve(),
						goalId ? ioGokr.fetchGoalActivity( { goalId, } ) : Promise.resolve(),
					];
					const [ teamsResponse, goalResponse, activityResponse, ] = await Promise.all( promises );
					commit( "receiveMyTeams", teamsResponse.data );
					if ( goalId ) {
						commit( "receiveGoal", goalResponse.data );
						commit( "receiveGoalActivity", activityResponse.data );
					}
					if ( teamId ) {
						commit( "assignGoalToTeam", teamId );
					}
				} catch ( e ) {
					error( "unexpectedError", e );
				} finally {
					commit( "resume" );
				}
			},
			async trySaveNewGoal( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { goalForm, materializedGoal: goal, } = getters;
					const { teamId, } = goal;
					const { comment, } = goalForm;

					// ioGokr.saveGoal() is used to persist brand-new goals
					const { data, } = await ioGokr.saveGoal( { goal, } );
					const { id: goalId, } = data;

					const trimmedComment = comment.trim();
					if ( trimmedComment ) {
						// eslint-disable-next-line no-magic-numbers
						await sleep( 500 );
						await ioGokr.createGoalComment( { goalId, comment: trimmedComment, } );
					}

					success( "createSuccessNotification" );
					// eslint-disable-next-line no-magic-numbers
					await sleep( 500 );
					navigation.store.navigateToGoal( { teamId, goalId, } );
				} catch ( e ) {
					error( "createErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async tryUpfetchGoal( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { goalForm, materializedGoal: goal, } = getters;
					const { goalId, revision, comment, } = goalForm;

					// ioGokr.upfetchGoal() is used to persist existing goals and then
					// immediately refetch the refreshed data
					const { data: nextGoal, } = await ioGokr.upfetchGoal( { goal, } );

					const trimmedComment = comment.trim();
					if ( trimmedComment ) {
						// eslint-disable-next-line no-magic-numbers
						await sleep( 500 );
						await ioGokr.createGoalComment( { goalId, comment: trimmedComment, } );
					}

					commit( `${ goalsStoreName }/selectTeam`, goal.teamId, { root: true, } );
					commit( "commitGoalForm", { goal: nextGoal, revision, } );
					success( "saveSuccessNotification" );
				} catch ( e ) {
					error( "saveErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async tryDeleteGoal( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { goalForm, } = getters;
					const { goalId, assignedTeamId: teamId, } = goalForm;

					await ioGokr.deleteGoal( { goalId, } );
					success( "deleteSuccessNotification" );
					// eslint-disable-next-line no-magic-numbers
					await sleep( 500 );
					navigation.store.navigateToGoals( { teamId, } );
				} catch ( e ) {
					error( "deleteErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async tryArchiveGoal( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { materializedGoal: goal, } = getters;
					const { teamId, } = goal;

					await ioGokr.archiveGoal( { goal, } );

					commit( `${ goalsStoreName }/selectTeam`, "", { root: true, } );
					success( "archiveSuccessNotification" );
					// eslint-disable-next-line no-magic-numbers
					await sleep( 500 );
					navigation.store.navigateToGoals( { teamId, } );
				} catch ( e ) {
					error( "archiveErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async tryUnarchiveGoal( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { goalForm, materializedGoal: goal, } = getters;
					const { id: goalId, teamId, } = goal;
					const { revision, } = goalForm;

					const { data: nextGoal, } = await ioGokr.unarchiveGoal( { goal, } );

					commit( `${ goalsStoreName }/selectTeam`, teamId, { root: true, } );
					commit( "commitGoalForm", { goal: nextGoal, revision, } );
					success( "unarchiveSuccessNotification" );
					// eslint-disable-next-line no-magic-numbers
					await sleep( 500 );
					navigation.store.navigateToGoal( { teamId, goalId, } );
				} catch ( e ) {
					error( "unarchiveErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async trySaveActivityComment( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { activityComment: comment, context, } = getters;
					const { goalId, } = context;

					await ioGokr.createGoalComment( { goalId, comment, } );

					success( "createCommentSuccessNotification" );
					commit( "updateActivityComment", { comment: "", } );
				} catch ( e ) {
					error( "createCommentErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
		},
	};
}
